/**
 * OneLattice Data Room design tokens.
 *
 * Visually KIN to the analyst console (`frontend/src/theme/tokens.ts`) — the same `--ol-*` token
 * language, the same calm desaturated surfaces — but tuned for a different audience: an applicant on
 * a phone, not an operator on a 12-hour SOC shift. So we default to the LIGHT palette (respecting the
 * OS `prefers-color-scheme` for a dark-mode preference), use a larger base font, and larger touch
 * targets. Both palettes are WCAG 2.2 AA. Colour is never the sole signal — every status pairs a hue
 * with an icon + text at the point of use.
 *
 * This module is the single source of truth. antdTheme.ts feeds these into AntD's ConfigProvider;
 * cssVars.ts projects them onto `--ol-*` custom properties consumed by global.css.
 */

export type ThemeMode = 'dark' | 'light';

/** Semantic role of a task/step status, used for pills and stepper dots. */
export type StatusIntent = 'positive' | 'attention' | 'negative' | 'neutral' | 'info' | 'processing';

export interface Palette {
  bgBase: string;
  bgContainer: string;
  bgElevated: string;
  bgSubtle: string;
  borderStrong: string;
  border: string;
  borderSubtle: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  focusRing: string;
  positive: string;
  attention: string;
  negative: string;
  info: string;
  processing: string;
}

/** Dark palette — kept identical to the analyst console so the surfaces read as one product. */
export const darkPalette: Palette = {
  bgBase: '#0f1418',
  bgContainer: '#171e24',
  bgElevated: '#1e272e',
  bgSubtle: '#212c34',
  borderStrong: '#39454e',
  border: '#2a343c',
  borderSubtle: '#222c33',
  textPrimary: '#e6edf3',
  textSecondary: '#a3b3c0',
  textTertiary: '#71818e',
  textInverse: '#0f1418',
  primary: '#4d9fd6',
  primaryHover: '#63b0e2',
  primaryActive: '#3d8cc2',
  focusRing: '#7cc0ff',
  positive: '#4aa564',
  attention: '#d1a23c',
  negative: '#d46a6a',
  info: '#5aa0dd',
  processing: '#8a7cd6',
};

/** Light palette — the DEFAULT for the applicant surface (daylight, phones, general public). */
export const lightPalette: Palette = {
  bgBase: '#f4f6f8',
  bgContainer: '#ffffff',
  bgElevated: '#ffffff',
  bgSubtle: '#eef1f4',
  borderStrong: '#c2ccd4',
  border: '#d8e0e6',
  borderSubtle: '#e6ecf0',
  textPrimary: '#1a2228',
  textSecondary: '#495663',
  textTertiary: '#6b7885',
  textInverse: '#ffffff',
  primary: '#1f6fb2',
  primaryHover: '#2a80c8',
  primaryActive: '#175c95',
  focusRing: '#1f6fb2',
  positive: '#2f8a4c',
  attention: '#9a6f16',
  negative: '#b23b3b',
  info: '#1f6fb2',
  processing: '#6a5bc0',
};

export const palettes: Record<ThemeMode, Palette> = {
  dark: darkPalette,
  light: lightPalette,
};

/**
 * Typography scale (px). Larger than the analyst console's dense 13px base — this is read by members
 * of the public on small screens, often in a hurry or under stress.
 */
export const typography = {
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Noto Sans Arabic', 'Noto Sans Devanagari', sans-serif",
  fontFamilyMono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  sizeXs: 12,
  sizeSm: 13,
  size: 15, // base — comfortable for the public
  sizeLg: 17,
  sizeH5: 18,
  sizeH4: 21,
  sizeH3: 25,
  sizeH2: 30,
  lineHeight: 1.55,
} as const;

/** 4px spacing grid. */
export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  pill: 999,
} as const;

export const intentColor = (intent: StatusIntent, p: Palette): string => {
  switch (intent) {
    case 'positive':
      return p.positive;
    case 'attention':
      return p.attention;
    case 'negative':
      return p.negative;
    case 'info':
      return p.info;
    case 'processing':
      return p.processing;
    case 'neutral':
    default:
      return p.textTertiary;
  }
};
